import { IResource } from "@gettactic/api";
import { useAuthenticated, useFloors } from "@gettactic/hooks";
import React from "react";
import { MapLoadingSpinner } from "@/features/map/components/MapView/MapLoadingSpinner";
import { MapViewSvg } from "@/features/map/components/MapView/MapViewSvg/MapViewSvg";
import { MapProvider, useMap } from "@/features/map/MapProvider";

export interface MapViewEmbeddedProps {
  officeId: string;
  floorId: string;
  focussedResourceId?: string;
  /** Map coordinates to centre/zoom on once the SVG is ready (e.g. a pin). */
  focusPoint?: { x: number; y: number };
  date?: string;
  /** End of the availability window checked against `date` — required for
   *  the schedulable-resources lookup, which errors without one. */
  endDate?: string;
  enableControls?: boolean;
  showMiniMap?: boolean;
  controlsPortalTarget?: HTMLElement | null;
  /** Overrides the target zoom used when focusing (lower = more zoomed out). */
  focusZoom?: number;
  /** Pulse-highlight `focussedResourceId` on load without panning/zooming
   *  the map to it. */
  disableInitialFocusZoom?: boolean;
  /** When set, clicking "Reserve" on a map spot calls this instead of
   *  opening this embedded instance's own (unmounted) reserve UI. */
  onReserveResource?: (resource: IResource) => void;
}

const MapViewEmbeddedInner: React.FC<MapViewEmbeddedProps> = ({
  focussedResourceId,
  focusPoint,
  enableControls,
  showMiniMap = true,
  controlsPortalTarget,
  focusZoom,
  disableInitialFocusZoom,
  onReserveResource
}) => {
  const { floor, isDataLoading } = useMap();

  if (isDataLoading || !floor?.id) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <MapLoadingSpinner />
      </div>
    );
  }

  return (
    <MapViewSvg
      embedded={true}
      allowFloorSwitcher={false}
      allowOverlays={enableControls}
      initialFocussedResource={focussedResourceId}
      initialFocussedPoint={focusPoint}
      allowInteractionsOnFocussedResource={true}
      showMiniMap={showMiniMap}
      controlsPortalTarget={controlsPortalTarget}
      focusZoom={focusZoom}
      disableInitialFocusZoom={disableInitialFocusZoom}
      onReserveResource={onReserveResource}
    />
  );
};

export const MapViewEmbedded: React.FC<MapViewEmbeddedProps> = (props) => {
  const { officeId, floorId } = props;
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const office = props?.officeId
    ? authenticatedUser?.offices?.byId?.[officeId]
    : null;

  const { floors: allFloors } = useFloors(officeId, null);

  const floor = allFloors?.find((f) => f.id === floorId);

  if (!office || !floor) return "Loading...";

  return (
    <MapProvider
      officeId={officeId}
      // Controlled: embedded maps have no floor switcher, so the floor must
      // always follow the floorId prop. Using only `initialFloorId` allows the
      // internal state to drift back to last-used / default-area after mount,
      // which was showing the wrong floor when the resource was on a different
      // floor than the user's current one.
      floorId={floorId}
      initialFloorId={floorId}
      startDate={props.date}
      initialEndDate={props.endDate}
      // The date must be part of the key: startDate is only read when the
      // provider mounts, so switching between two reservations on the SAME
      // spot but different days would otherwise keep showing the first
      // day's schedules.
      key={`${props.focussedResourceId}-${props.focusPoint?.x}-${props.focusPoint?.y}-${floorId}-${props.date ?? ""}-${props.endDate ?? ""}`}
      isEmbedded
      disableUrlOfficeSync={true}
    >
      <MapViewEmbeddedInner {...props} />
    </MapProvider>
  );
};
